import qs from "query-string";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import BankFeedsCommonLayout from "../../components/bankFeeds/layout";
import { isDev, STORES } from "../../utils";
import { BankFeedAccountLinkingData } from "./constants";
import { ErrorComponent } from "./ErrorComponent";
import { GenericBankFeedsLinkStepOneComponent } from "./GenericBankFeedsLinkStepOneComponent";
import { GenericBankFeedsLinkStepThreeComponent } from "./GenericBankFeedsLinkStepThreeComponent";
import { GenericBankFeedsLinkStepTwoAlternateComponent } from "./GenericBankFeedsLinkStepTwoAlternateComponent";
import { GenericBankFeedsLinkStepTwoComponent } from "./GenericBankFeedsLinkStepTwoComponent";
import { RetryableErrorModal } from "./RetryableErrorModal";

const GenericBankFeedsLink: React.FC = () => {
  const history = useHistory();
  const { search } = history.location;
  const parsedSearch: any = qs.parse(search);
  const { token, prelinked, restart, disablePopup } = parsedSearch;
  const overrideExisting = parsedSearch.override_existing === "true";

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [retryCallback, setRetryCallback] = useState<() => void>(() => {});
  const [retryableErrorMessage, setRetryableErrorMessage] =
    useState<string>("");

  const [step, setStep] = useState<number>(1);
  const [store, setStore] = useState<STORES | null>(null);
  const [complete, setComplete] = useState(false);
  const [platformName, setPlatformName] = useState<string | null>(null);
  const [transactionStartDate, setTransactionStartDate] = useState<
    string | null
  >(null);
  const [bankFeedAccountLinkingData, setBankFeedAccountLinkingData] = useState<
    BankFeedAccountLinkingData[]
  >([]);

  useEffect(() => {
    document.title = "Connect Your Financial Accounts";
  }, []);

  // If user tries to exit the page, show a popup warning.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (
        !complete &&
        !errorMessage &&
        !retryableErrorMessage &&
        (!isDev() || disablePopup !== "true")
      ) {
        // If there is an error message, we should allow reload without the popup warning.
        e.preventDefault();
        e.returnValue = "";
        // Chrome requires returnValue to be set.
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [complete, disablePopup, errorMessage, retryableErrorMessage]);

  const getComponentFromStep = () => {
    if (!token) {
      return <ErrorComponent errorMessage="Access denied."></ErrorComponent>;
    }

    if (errorMessage) {
      return <ErrorComponent errorMessage={errorMessage}></ErrorComponent>;
    }

    switch (step) {
      case 1:
        return (
          <GenericBankFeedsLinkStepOneComponent
            token={token}
            store={store}
            setStep={setStep}
            setStore={setStore}
            prelinked={prelinked}
            platformName={platformName}
            setPlatformName={setPlatformName}
            setErrorMessage={setErrorMessage}
            setRetryCallback={setRetryCallback}
            setRetryableErrorMessage={setRetryableErrorMessage}
            setConfirmedBankFeedAccountLinkingData={
              setBankFeedAccountLinkingData
            }
          />
        );
      case 2:
        if (store === STORES.XERO) {
          return (
            <GenericBankFeedsLinkStepTwoAlternateComponent
              setStep={setStep}
              setErrorMessage={setErrorMessage}
              setTransactionStartDate={setTransactionStartDate}
              confirmedBankFeedAccountLinkingData={bankFeedAccountLinkingData}
            />
          );
        }
        return (
          <GenericBankFeedsLinkStepTwoComponent
            setStep={setStep}
            setRetryCallback={setRetryCallback}
            setTransactionStartDate={setTransactionStartDate}
            setRetryableErrorMessage={setRetryableErrorMessage}
            confirmedBankFeedAccountLinkingData={bankFeedAccountLinkingData}
          />
        );
      case 3:
        return (
          <GenericBankFeedsLinkStepThreeComponent
            token={token}
            store={store}
            restart={restart}
            setStep={setStep}
            setComplete={setComplete}
            platformName={platformName}
            setErrorMessage={setErrorMessage}
            setRetryCallback={setRetryCallback}
            transactionStartDate={transactionStartDate}
            setRetryableErrorMessage={setRetryableErrorMessage}
            bankFeedAccountLinkingData={bankFeedAccountLinkingData}
            overrideExisting={overrideExisting}
          />
        );
      default:
        return <ErrorComponent errorMessage="Access denied."></ErrorComponent>;
    }
  };

  return (
    <BankFeedsCommonLayout errorMessage="">
      {getComponentFromStep()}
      <RetryableErrorModal
        errorMessage={retryableErrorMessage}
        retryCallback={retryCallback}
        setRetryableErrorMessage={
          setRetryableErrorMessage
        }></RetryableErrorModal>
    </BankFeedsCommonLayout>
  );
};

export default GenericBankFeedsLink;
